import {
    Box,
	Chip,
    CircularProgress,
    Icon,
    IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
	TablePagination,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import { EditOutlined, InfoOutlined, MoreVert, SearchOutlined, SendOutlined } from "@mui/icons-material";
import "moment/locale/pt-br";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useProfile } from "../../../context/ProfileContext";
import { ToastContent } from "../../../shared/components";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../shared/enums/profiles";
import { Environment } from "../../../shared/environment";
import { checkByProfileGuard, ProfileGuard } from "../../../shared/guards/ProfileGuard";
import { useDebounce, useToast } from "../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { contractService, IListagemContract, TContractAllPromise } from "../../../shared/services/api/contract/contractService";
import { IMessage } from "../../../shared/types/message";
import { translate } from "../../../shared/utils";
import { formatDateBRHasMinutes } from "../../../shared/utils/format-date";
import { returnMessageError } from "../../../shared/utils/returnMessageError";
import { ToolbarListagemContratos } from "./ToolbarListagemContratos";

const contractStatusChipSx = (status: string) => {
	switch (status) {
		case "active":
			return {
				color: "#0f8a0d",
				fontWeight: 500,
				background: "#d9f8e5",
				border: "0.5px solid #f1f1f1",
			};
		case "inactive":
			return {
				color: "#202020",
				fontWeight: 500,
				background: "#f0f0f0",
				border: "0.5px solid #f1f1f1",
			};
		case "blocked":
			return {
				color: "#b26a00",
				fontWeight: 500,
				background: "#fff3cd",
				border: "0.5px solid #f1f1f1",
			};
		case "sent":
			return {
				color: "#0170B3",
				fontWeight: 500,
				background: "#e3f2fd",
				border: "0.5px solid #f1f1f1",
			};
		case "canceled":	
		default:
			return {
				color: "#e40909",
				fontWeight: 500,
				background: "#fce4e4",
				border: "0.5px solid #f1f1f1",
			};
	}
};

export const ListagemContratosPresentation: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { debounce } = useDebounce();
	const navigate = useNavigate();
	const { permissions, registerClient, permissionLevel, isSuperAdmin } = useProfile();
	const [rows, setRows] = useState<IListagemContract[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(null);
	const [loadingShootId, setLoadingShootId] = useState<string | null>(null);
	const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
	const [actionsRowId, setActionsRowId] = useState<string | null>(null);
	const [totalCount, setTotalCount] = useState(0);
	const toastHandler = useToast();

	const actionsMenuOpen = Boolean(actionsAnchorEl);
	const currentActionsRow = useMemo(() => {
		if (!actionsRowId) return null;
		return rows.find((r) => r.id === actionsRowId) ?? null;
	}, [actionsRowId, rows]);

	const openActionsMenu = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
		setActionsAnchorEl(event.currentTarget);
		setActionsRowId(rowId);
	};

	const closeActionsMenu = () => {
		setActionsAnchorEl(null);
		setActionsRowId(null);
	};

	const hasContractsOptionsModule = useMemo(() => {
		return Object.prototype.hasOwnProperty.call(permissions ?? {}, PermissionRolesEnum.ContractsOptions);
	}, [permissions]);

	const hasViewPermission = useMemo(() => {
		if (hasContractsOptionsModule) {
			return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.Contracts);
		}
		return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts);
	}, [hasContractsOptionsModule]);

	const canViewContractDetails = useMemo(() => {
		if (hasContractsOptionsModule) {
			return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsDetails);
		}
		// legado não tinha permissão específica de detalhes
		return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.Contracts);
	}, [hasContractsOptionsModule]);

	const canAddContract = useMemo(() => {
		if (hasContractsOptionsModule) {
			return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsAdd);
		}
		return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsAdd);
	}, [hasContractsOptionsModule]);

	const canEditContract = useMemo(() => {
		if (hasContractsOptionsModule) {
			return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsUpdate);
		}
		return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsUpdate);
	}, [hasContractsOptionsModule]);

	const canShootContract = useMemo(() => {
		if (hasContractsOptionsModule) {
			return checkByProfileGuard(PermissionRolesEnum.ContractsOptions, PermissionActionsEnum.ContractsShoot);
		}
		return checkByProfileGuard(PermissionRolesEnum.ClientsOptions, PermissionActionsEnum.ContractsShoot);
	}, [hasContractsOptionsModule]);

	const pagina = useMemo(() => {
		return Number(searchParams.get("pagina") || "1");
	}, [searchParams]);

	const rowsPerPage = useMemo(() => {
		return Number(searchParams.get("rowsPerPage") || "10");
	}, [searchParams]);

	const status = useMemo(() => {
		return searchParams.get("status") || "Todos";
	}, [searchParams]);

	const dataCreate = useMemo(() => {
		return searchParams.get("dataCreate") || "";
	}, [searchParams]);

	const title = useMemo(() => {
		return searchParams.get("title") || "";
	}, [searchParams]);

	const [titleDraft, setTitleDraft] = useState("");
	const [statusDraft, setStatusDraft] = useState("Todos");
	const [dataCreateDraft, setDataCreateDraft] = useState("");

	useEffect(() => {
		setTitleDraft(title);
		setStatusDraft(status || "Todos");
		setDataCreateDraft(dataCreate);
	}, [title, status, dataCreate]);

	useEffect(() => {
		const currentRowsPerPage = searchParams.get("rowsPerPage");
		if (currentRowsPerPage) return;

		setSearchParams(
			{
				pagina: String(pagina),
				rowsPerPage: "10",
				title,
				status,
				dataCreate,
			},
			{ replace: true }
		);
	}, [searchParams, setSearchParams, pagina, title, status, dataCreate]);

	const updateParams = (next: { pagina?: string; rowsPerPage?: string; title?: string; status?: string; dataCreate?: string }) => {
		setSearchParams(
			{
				pagina: String(pagina),
				rowsPerPage: String(rowsPerPage),
				title,
				status,
				dataCreate,
				...next,
			},
			{ replace: true }
		);
	};

	useEffect(() => {
		if (!hasViewPermission) return;
		setIsLoading(true);

		debounce(() => {
			contractService
				.getAll(pagina, title, status === "Todos" ? "" : status, dataCreate, rowsPerPage)
				.then((result: TContractAllPromise) => {
					setIsLoading(false);

					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
						return;
					}

					setTotalCount(result.total);
					setRows(result.results);
				})
				.catch((err: any) => {
					setIsLoading(false);
					const { titleMessage, contentMessage } = returnMessageError(err);

					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		});
	}, [hasViewPermission, pagina, rowsPerPage, title, status, dataCreate]);

	const handleViewDocumentContract = (id: string) => {
		setLoadingDocumentId(id);

		debounce(() => {
			contractService
				.getDocumentContract(id)
				.then((result: void | Error) => {
					setLoadingDocumentId(null);

					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					}
				})
				.catch((err: any) => {
					setLoadingDocumentId(null);

					const { titleMessage, contentMessage } = returnMessageError(err);

					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		});
	};

	const handleShootQueue = (id: string) => {
		setLoadingShootId(id);

		debounce(() => {
			contractService
				.shootQueue(id)
				.then((result: IMessage<null> | Error) => {
					setLoadingShootId(null);

					if (result instanceof Error) {
						const titleMessage = "Alerta!!";
						const contentMessage = "Erro: " + result?.message;

						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
						});
					} else {
						toastHandler.present({
							type: "success",
							position: "top-right",
							messageOrContent: <ToastContent title={result.message!} content={result.description!} />,
						});

						// Recarrega a listagem mantendo os filtros atuais
						updateParams({});
					}
				})
				.catch((err: any) => {
					setLoadingShootId(null);
					const { titleMessage, contentMessage } = returnMessageError(err);

					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
					});
				});
		});
	};

	return (
		<LayoutBaseDePagina
			titulo={"Contratos"}
			subtitulo={"Listagem de contratos"}
			barraDeFerramentas={
				<ToolbarListagemContratos
					searchText={titleDraft}
					status={statusDraft}
					dataCreate={dataCreateDraft}
					isSearching={isLoading}
					onChangeSearchText={setTitleDraft}
					onChangeStatus={setStatusDraft}
					onChangeDataCreate={setDataCreateDraft}
					onSearch={() => updateParams({ pagina: "1", title: titleDraft, status: statusDraft, dataCreate: dataCreateDraft })}
					onClearSearch={() => {
						setTitleDraft("");
						updateParams({ pagina: "1", title: "" });
					}}
					showButtonNew={canAddContract}
					onClickNew={() => navigate("/clientes/contratos/detalhe/novo")}
				/>
			}
		>
			{hasViewPermission && (
				<TableContainer
					component={Paper}
					variant="elevation"
					sx={{
						mt: 1,
						width: "96%",
						px: { xs: 1, sm: 3 },
						py: { xs: 2, sm: 3 },
						ml: 1,
						borderRadius: 2,
						overflow: "hidden",
					}}
					aria-label="customized table"
				>
					<Table size="small" stickyHeader aria-label="tabela de contratos">
						<TableHead>
							<TableRow sx={{ backgroundColor: "grey.100" }}>
								<TableCell>
									<Typography variant="button">Título</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Data de criação</Typography>
								</TableCell>
								<TableCell align="center">
									<Typography variant="button">Status</Typography>
								</TableCell>
								<TableCell align="right">
									<Typography variant="button">Ações</Typography>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.length === 0 && !isLoading && (
								<TableRow>
											<TableCell colSpan={4} align="center">
										Nenhum registro
									</TableCell>
								</TableRow>
							)}

							{rows?.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell>
											<Typography variant="body2" color="text.secondary">
												{row.title}
											</Typography>
										</TableCell>

										<TableCell align="center">
											<Typography variant="body2" color="text.secondary">
												{formatDateBRHasMinutes(row.createdAt)}
											</Typography>
										</TableCell>

										<TableCell align="center">
											<Chip size="small" label={translate(row.statusContract)} sx={contractStatusChipSx(row.statusContract)} />
										</TableCell>

										<TableCell align="right">
											<Tooltip title="Ações" placement="top">
												<span>
													<IconButton disabled={isLoading} size="small" onClick={(e) => openActionsMenu(e, row.id)}>
														<MoreVert fontSize="small" />
													</IconButton>
												</span>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>

						<Menu
							anchorEl={actionsAnchorEl}
							open={actionsMenuOpen}
							onClose={closeActionsMenu}
							anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
							transformOrigin={{ vertical: "top", horizontal: "right" }}
						>
							{hasViewPermission && (
								<MenuItem
									disabled={isLoading || !actionsRowId || !!loadingDocumentId}
									onClick={() => {
										const rowId = actionsRowId;
										closeActionsMenu();
										if (rowId) handleViewDocumentContract(rowId);
									}}
								>
									<ListItemIcon>
										{loadingDocumentId && actionsRowId && loadingDocumentId === actionsRowId ? (
											<CircularProgress size={18} />
										) : (
											<SearchOutlined fontSize="small" sx={{ color: "#7924c7" }} />
										)}
									</ListItemIcon>
									<ListItemText>Visualizar</ListItemText>
								</MenuItem>
							)}

							{canViewContractDetails && (
								<MenuItem
									disabled={isLoading || !actionsRowId}
									onClick={() => {
										const rowId = actionsRowId;
										closeActionsMenu();
										if (rowId) navigate(`/clientes/contratos/detalhes/${rowId}`);
									}}
								>
									<ListItemIcon>
										<InfoOutlined fontSize="small" sx={{ color: "primary.main" }} />
									</ListItemIcon>
									<ListItemText>Detalhes</ListItemText>
								</MenuItem>
							)}

							{canEditContract && (
								<MenuItem
									disabled={isLoading || !actionsRowId}
									onClick={() => {
										const rowId = actionsRowId;
										closeActionsMenu();
										if (rowId) navigate(`/clientes/contratos/detalhe/${rowId}`);
									}}
								>
									<ListItemIcon>
										<EditOutlined fontSize="small" sx={{ color: "#0170B3" }} />
									</ListItemIcon>
									<ListItemText>Editar</ListItemText>
								</MenuItem>
							)}

							{canShootContract && (
								<MenuItem
									disabled={
										isLoading ||
										!actionsRowId ||
										!!loadingShootId ||
										currentActionsRow?.statusContract !== "active"
									}
									onClick={() => {
										const rowId = actionsRowId;
										closeActionsMenu();
										if (rowId) handleShootQueue(rowId);
									}}
								>
									<ListItemIcon>
										{loadingShootId && actionsRowId && loadingShootId === actionsRowId ? (
											<CircularProgress size={18} />
										) : (
											<SendOutlined fontSize="small" sx={{ color: "primary.main" }} />
										)}
									</ListItemIcon>
									<ListItemText>Disparar</ListItemText>
								</MenuItem>
							)}
						</Menu>

					{totalCount === 0 && !isLoading && <caption>{Environment.LISTAGEM_VAZIA}</caption>}

					<LoadingComp isopen={isLoading} />

					<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
						<TablePagination
							component="div"
							count={totalCount}
							page={Math.max(pagina - 1, 0)}
							rowsPerPage={rowsPerPage}
							onPageChange={(_, newPage) => updateParams({ pagina: String(newPage + 1) })}
							onRowsPerPageChange={(e) => updateParams({ pagina: "1", rowsPerPage: e.target.value })}
							rowsPerPageOptions={[10, 25, 50]}
							labelRowsPerPage="Linhas por página:"
							labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
						/>
					</Box>
				</TableContainer>
			)}
		</LayoutBaseDePagina>
	);
};
